import { getThemeLayoutClass } from '../theme/crystalLayout';

import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { Page } from '../types';
import { ChevronRight, Home, Link as LinkIcon, Layers, Zap, Info, ArrowRight, MapPin } from 'lucide-react';
import * as motion from 'motion/react-m';
import { fetchResolvedPageContext, navigateResolvedPage } from '../domain/resolvedPageContext';

const RelatedMedia: React.FC<{ src?: string | null; alt: string; className: string }> = ({ src, alt, className }) => {
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const imageUrl = typeof src === 'string' ? src.trim() : '';
    if (!imageUrl || imageUrl === failedSrc) {
        return <div aria-hidden="true" className="w-full h-full flex items-center justify-center rounded-2xl bg-slate-50 text-slate-300"><Layers size={28} /></div>;
    }
    return <img src={imageUrl} alt={alt} className={className} onError={() => setFailedSrc(imageUrl)} />;
};

// --- Semantic Breadcrumbs ---
export const SemanticBreadcrumbs: React.FC<{ items: { label: string; page?: Page; url?: string; active?: boolean }[] }> = ({ items }) => {
    const context = useContext(AppContext);
    const navigateToEntity = context?.navigateToEntity;

    const handleNavigate = (item: { label: string; page?: Page; url?: string; active?: boolean }) => {
        if (item.active) return;
        if (item.url?.startsWith('/')) {
            void fetchResolvedPageContext(item.url).then((resolved) => navigateResolvedPage(resolved));
        } else if (item.url) {
            if (item.url.startsWith('/')) {
                return;
            } else {
                window.open(item.url, '_blank', 'noreferrer');
            }
        }
    };

    return (
        <nav className="flex items-center gap-2 text-[10px] md:text-[11px] text-gray-500 mb-6 overflow-x-auto no-scrollbar whitespace-nowrap bg-white/50 backdrop-blur-md py-2 px-4 rounded-full border border-gray-100 shadow-sm w-full max-w-full min-w-0" aria-label="Breadcrumb">
            <a
                href="/"
                onClick={(event) => { event.preventDefault(); void navigateToEntity?.('HOME'); }}
                className="hover:text-blue-600 transition-colors flex items-center gap-1.5 group"
                aria-label="Accueil Mobilbo"
            >
                <Home size={12} className="group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Accueil Mobilbo</span>
            </a>
            
            {items.map((item, idx) => (
                <React.Fragment key={idx}>
                    <span className="text-gray-300 select-none"><ChevronRight size={10} /></span>
                    {item.active ? <span
                        className={`transition-all duration-200 shrink-0 ${item.active ? 'text-gray-900 font-extrabold cursor-default' : 'hover:text-blue-600 hover:translate-x-0.5 cursor-pointer'}`}
                    >
                        {item.label}
                    </span> : <a
                        href={item.url || undefined} aria-disabled={!item.url}
                        onClick={(event) => { event.preventDefault(); handleNavigate(item); }}
                        className="transition-all duration-200 hover:text-blue-600 hover:translate-x-0.5 cursor-pointer"
                    >{item.label}</a>}
                </React.Fragment>
            ))}
        </nav>
    );
};

// --- Semantic Topic Clusters ---
export const TopicClusters: React.FC = () => {
    const context = useContext(AppContext);
    
    const clusters = [
        { label: 'Réparations', icon: <Zap size={14} />, page: Page.Repairs, url: '/reparation', count: 'Choisir un service' },
        { label: 'Boutique Smartphones', icon: <Layers size={14} />, page: Page.BuyPhone, url: '/acheter', count: 'Voir les produits disponibles' },
        { label: 'Rachat', icon: <ArrowRight size={14} />, page: Page.SellPhone, url: '/rachat', count: 'Demander une estimation' },
        { label: 'Accessoires', icon: <LinkIcon size={14} />, page: Page.Accessories, url: '/accessoires', count: 'Voir les accessoires' },
      { label: 'Blog Tech', icon: <Info size={14} />, url: '/blog', count: 'Lire les conseils experts' },
    { label: 'Appareils', icon: <Layers size={14} />, url: '/appareils', count: 'Explorer les modèles' },
    { label: 'Identifier', icon: <MapPin size={14} />, url: '/aide', count: 'Identifier un appareil' },
    { label: 'Diagnostic', icon: <Zap size={14} />, url: '/imei', count: 'Lancer un diagnostic' },
    { label: 'Contact', icon: <MapPin size={14} />, url: '/contact', count: 'Contacter la boutique' },
  ];

    if (!context) return null;
    const { navigateToEntity } = context;

    return (
        <div className={getThemeLayoutClass(context?.activeThemeConfig, 'discoveryContainer')}>
            <h2 className={getThemeLayoutClass(context?.activeThemeConfig, 'discoveryTitle')}>
                <span className="w-12 h-px bg-gray-200"></span>
                Explorer nos services
                <span className="w-12 h-px bg-gray-200"></span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {clusters.map((cluster, idx) => (
                    <motion.a
                        key={idx}
                        href={cluster.url}
                        whileHover={{ y: -5, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(event) => {
                            event.preventDefault();
                            const archetype = cluster.page === Page.Repairs
                                ? 'REPAIR_ROOT'
                                : cluster.page === Page.Blog
                                    ? 'BLOG_ROOT'
                                    : cluster.page === Page.SellPhone
                                        ? 'RACHAT'
                                        : cluster.page === Page.Accessories
                                            ? 'ACCESSORY_CATALOG_ROOT'
                                            : 'PHONE_CATALOG_ROOT';
                            void navigateToEntity(archetype);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={getThemeLayoutClass(context?.activeThemeConfig, 'discoveryCard')}
                    >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/30 rounded-full blur-3xl -mr-10 -mt-10 group-hover:bg-blue-100/50 transition-colors"></div>
                        <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500">
                            {cluster.icon}
                        </div>
                        <div className="font-black text-gray-900 text-sm uppercase tracking-tight mb-1">{cluster.label}</div>
                        <div className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">{cluster.count}</div>
                    </motion.a>
                ))}
            </div>

            {/* Local SEO Footer Links */}
            <div className={getThemeLayoutClass(context?.activeThemeConfig, 'discoveryLocations')}>
                <div className="flex items-center gap-1.5"><MapPin size={12} className="text-blue-500" /> votre région</div>
                <div className="flex items-center gap-1.5"><MapPin size={12} className="text-blue-500" /> Sion</div>
                <div className="flex items-center gap-1.5"><MapPin size={12} className="text-blue-500" /> Monthey</div>
                <div className="flex items-center gap-1.5"><MapPin size={12} className="text-blue-500" /> Valais Central</div>
                <div className="flex items-center gap-1.5"><MapPin size={12} className="text-blue-500" /> Suisse Romande</div>
            </div>
        </div>
    );
};

// --- Related Content Module ---
// Renders only relationships that already have an explicit database identity.
// Route paths come from the existing public projections; this component never derives slugs.
export const RelatedContent: React.FC<{
    currentModelId?: string | number | null;
    deviceKnowledgePath?: string | null;
    repairModelPath?: string | null;
    type: 'product';
}> = ({ currentModelId, deviceKnowledgePath, repairModelPath }) => {
    const context = useContext(AppContext);
    const normalizedModelId = currentModelId == null ? '' : String(currentModelId);

    const linkedModel = useMemo(() => {
        if (!context || !normalizedModelId) return null;
        for (const brand of context.repairData || []) {
            const model = brand.models.find(candidate => String(candidate.id) === normalizedModelId);
            if (model) return { brand, model };
        }
        return null;
    }, [context, normalizedModelId]);

    const relatedBlogs = useMemo(() => {
        if (!context || !normalizedModelId) return [];
        return (context.blogPosts || []).filter(post => {
            if (!post.canonicalPath || post.status === 'draft') return false;
            return (post.governedAssociations || []).some(association =>
                association.entityType === 'MODEL'
                && (
                    String(association.entityId) === normalizedModelId
                    || (linkedModel?.model?.masterStableId && String(association.entityId) === String(linkedModel.model.masterStableId))
                )
            );
        }).slice(0, 3);
    }, [context, normalizedModelId, linkedModel?.model?.masterStableId]);

    const repairPath = String(repairModelPath || linkedModel?.model?.canonicalPath || '').trim();
    const devicePath = String(deviceKnowledgePath || '').trim();
    if (!context || !normalizedModelId || (!repairPath && !devicePath && relatedBlogs.length === 0)) return null;

    return (
        <div className="space-y-12 mt-20" data-governed-related-content={normalizedModelId}>
            {(repairPath || devicePath) && (
                <section>
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-1.5 h-8 bg-blue-500 rounded-full"></div>
                        <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">
                            Services pour <span className="text-gray-400">{linkedModel?.model?.name || 'cet appareil'}</span>
                        </h2>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                    {devicePath && <a href={devicePath} data-governed-device-knowledge-link={normalizedModelId} className="block p-4 bg-white border border-gray-100 rounded-3xl text-center hover:border-blue-200 hover:shadow-lg transition-all group">
                        <div className="h-24 flex items-center justify-center mb-3">
                            <RelatedMedia src={linkedModel?.model?.image} alt={linkedModel?.model?.altText || `Informations ${linkedModel?.model?.name || ''}`} className="h-full object-contain group-hover:scale-110 transition-transform" />
                        </div>
                        <span className="text-[11px] font-black uppercase text-gray-900 tracking-tight">Voir la fiche appareil {linkedModel?.model?.name || ''}</span>
                    </a>}
                    {repairPath && <a href={repairPath} data-governed-model-link={normalizedModelId} className="block p-4 bg-white border border-gray-100 rounded-3xl text-center hover:border-blue-200 hover:shadow-lg transition-all group">
                        <div className="h-24 flex items-center justify-center mb-3">
                            <RelatedMedia src={linkedModel?.model?.image} alt={linkedModel?.model?.altText || `Réparation ${linkedModel?.model?.name || ''}`} className="h-full object-contain group-hover:scale-110 transition-transform" />
                        </div>
                        <span className="text-[11px] font-black uppercase text-gray-900 tracking-tight">Réparer {linkedModel?.model?.name || ''}</span>
                    </a>}
                    </div>
                </section>
            )}

            {relatedBlogs.length > 0 && (
                <section>
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-1.5 h-8 bg-emerald-500 rounded-full"></div>
                        <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">Conseils <span className="text-gray-400">associés</span></h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {relatedBlogs.map(post => (
                            <a key={post.id} href={post.canonicalPath} data-governed-article-link={post.id} className="p-5 bg-white border border-gray-100 rounded-3xl hover:border-emerald-200 hover:shadow-lg transition-all">
                                <span className="text-sm font-black text-gray-900">{post.title}</span>
                            </a>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};
